import { DrawerActions } from "@react-navigation/native";
import { DrawerContentComponentProps } from "@react-navigation/drawer";
import { Drawer } from "expo-router/drawer";
import { StatusBar } from "expo-status-bar";
import React from "react";
import { Platform, useWindowDimensions, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { MembershipActivationTrigger } from "@/api/tenants/MembershipActivationTrigger";
import { useAuth } from "@/auth/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RootCrashFallback } from "@/components/RootCrashFallback";
import { WIDE_VIEWPORT_BREAKPOINT } from "@/constants/layout";
import { OfflineSyncTrigger } from "@/offline/OfflineSyncTrigger";
import { useTheme } from "@/theme/ThemeProvider";
import { OfflineBanner } from "@/ui/components/OfflineBanner";
import { FinderAppChrome } from "@/ui/finder/FinderAppChrome";
import { FinderModeProvider, useFinderMode } from "@/ui/finder/FinderModeContext";
import { FinderGateFallback } from "@/ui/finder/FinderGateFallback";
import { FinderModeGate } from "@/ui/finder/FinderModeGate";
import { AppBottomBar } from "@/ui/layout/AppBottomBar";
import AppDrawerContent from "@/ui/layout/AppDrawerContent";
import { FabSlotProvider, FabSlotRenderer } from "@/ui/layout/FabSlot";
import { OpenDrawerContext, OpenDrawerProvider } from "@/ui/layout/OpenDrawerContext";

const SIDEBAR_WIDTH = 270; // 10 % schmaler als 300
/** Ab WIDE_VIEWPORT_BREAKPOINT: Navigation dauerhaft sichtbar („breites Viewport“). Darunter: Burger-Menü. */

/** Wenn true, ist der Drawer-Header sichtbar (Burger-Modus) – Seiten können dann ihre eigene Hauptüberschrift weglassen. */
export const AppHeaderVisibleContext = React.createContext(false);

/**
 * Die untere Leiste sitzt außerhalb des Drawers. Ohne diese Brücke bleibt
 * openDrawer die leere Funktion aus dem Kontext, und „Menü“ tut nichts.
 */
function DrawerWithOpener(props: DrawerContentComponentProps) {
  const { registerOpenDrawer } = React.useContext(OpenDrawerContext);
  React.useEffect(() => {
    registerOpenDrawer(() => {
      props.navigation.dispatch(DrawerActions.openDrawer());
    });
  }, [props.navigation, registerOpenDrawer]);
  return <AppDrawerContent {...props} />;
}

/** Mindesthöhe für den schwarzen Streifen unten (Android-Tablet: insets.bottom ist oft 0). */
const ANDROID_NAV_STRIP_MIN_HEIGHT = 48;

export default function AppLayout() {
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();

  const isWide = width >= WIDE_VIEWPORT_BREAKPOINT;
  const sidebarWidth = isWide ? 288 : Math.min(SIDEBAR_WIDTH, width * 0.765); // 288 = 320×0.9, 0.765 = 0.85×0.9
  const appHeaderVisible = !isWide;
  const bottomStripHeight =
    Platform.OS === "web" ? 0 : Math.max(insets.bottom, ANDROID_NAV_STRIP_MIN_HEIGHT);

  return (
    <AppShellBoundary>
      <FinderModeProvider userId={user?.id ?? null}>
        <AppShell
          appHeaderVisible={appHeaderVisible}
          isWide={isWide}
          sidebarWidth={sidebarWidth}
          bottomStripHeight={bottomStripHeight}
        />
      </FinderModeProvider>
    </AppShellBoundary>
  );
}

/** App-Shell-Boundary: Absturz der angemeldeten Hülle, mit Abmelden und Neu laden. */
function AppShellBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(_error, resetError) => <RootCrashFallback resetError={resetError} />}
    >
      {children}
    </ErrorBoundary>
  );
}

function AppShell({
  appHeaderVisible,
  isWide,
  sidebarWidth,
  bottomStripHeight,
}: {
  appHeaderVisible: boolean;
  isWide: boolean;
  sidebarWidth: number;
  bottomStripHeight: number;
}) {
  const { theme } = useTheme();
  const { blocking: finderActive } = useFinderMode();

  return (
    <AppHeaderVisibleContext.Provider value={appHeaderVisible}>
      <StatusBar
        style={theme.mode === "dark" ? "light" : "dark"}
        backgroundColor={theme.color.bg}
      />
      <OpenDrawerProvider>
       <FabSlotProvider>
        <MembershipActivationTrigger />
        <OfflineSyncTrigger />
        <View
          style={[
            { flex: 1, minWidth: 0, maxWidth: "100%", overflow: "hidden", backgroundColor: theme.color.bg },
            Platform.OS === "web" && { width: "100%" as unknown as number },
          ]}
          {...(Platform.OS === "web" ? { dataSet: { fcShell: "1" } } : {})}
        >
          <FinderAppChrome
            finderActive={finderActive}
            isWide={isWide}
            bottomBar={<AppBottomBar />}
          >
          <OfflineBanner />
          <View style={{ flex: 1, minWidth: 0, minHeight: 0, maxWidth: "100%", overflow: "hidden" }}>
            {/* Wrapper mit expliziter Höhe, damit Drawer-Inhalt auf Web/Tablet sichtbar wird */}
            <View style={{ flex: 1, minHeight: 0 }}>
            <Drawer
            screenOptions={{
              headerShown: false,
              swipeEnabled: !finderActive && !isWide,
              drawerType: isWide ? "permanent" : "front",
              drawerStyle: {
                width: sidebarWidth,
                backgroundColor: theme.color.panelContentBg,
                borderRightColor: theme.color.border,
                borderRightWidth: 1,
              },
              overlayColor: isWide ? "transparent" : theme.color.overlay,
              // Wichtig für Web/Tablet: Inhaltsfläche braucht explizit flex:1, sonst bleibt sie leer. Auf Tablet: dünner Rahmen oben/unten wie Trennlinie zum Drawer.
              sceneStyle: isWide
                ? {
                    flex: 1,
                    minWidth: 0,
                    borderTopWidth: 1,
                    borderBottomWidth: 1,
                    borderColor: theme.color.border,
                  }
                : { flex: 1, minWidth: 0, maxWidth: "100%", overflow: "hidden" },
            }}
            drawerContent={(props) => <DrawerWithOpener {...props} />}
          >
            <Drawer.Screen name="home" />
            <Drawer.Screen name="my" />
            <Drawer.Screen name="new-animal" options={{ drawerItemStyle: { display: "none" } }} />
            <Drawer.Screen name="t/[tenantID]" options={{ drawerItemStyle: { display: "none" } }} />
          </Drawer>
            </View>
            {/* FAB schwebt über dem Inhalt, aber innerhalb des korrekt bemessenen Bereichs (über der Bottom-Bar, unterhalb des Offline-Banners). */}
            {!finderActive && <FabSlotRenderer />}
          </View>
          {/* Weißer Streifen unten: Platz für Android-System-Buttons (Safe Area). */}
          {bottomStripHeight > 0 && (
            <View
              style={{
                height: bottomStripHeight,
                backgroundColor: theme.color.bg,
              }}
            />
          )}
          </FinderAppChrome>
          <ErrorBoundary
            fallback={(_error, resetError) => <FinderGateFallback resetError={resetError} />}
          >
            <FinderModeGate />
          </ErrorBoundary>
        </View>
       </FabSlotProvider>
      </OpenDrawerProvider>
    </AppHeaderVisibleContext.Provider>
  );
}
